import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useBloc } from '@core/utils/bloc';
import { SystemCalendarBloc } from './SystemCalendarBloc';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  Calendar,
  CalendarEvent,
  CalendarView,
  countBusinessDays,
  expandHolidays,
  HolidaySet,
  holidayCalendarEvents,
  isHolidayCalendarItem,
  isWorkingDay,
  snapToWorkingDay,
  splitIntoWeekdaySegments,
  WeekdaySegment,
} from '../Calendar';
import { showYesNo } from '../alerts';
import { useLazyEffect } from '@core/utils/react';
import { formControl, formGroup } from '@core/utils/form';
import { CBody } from '../CBody';
import useWindowDimensions from '@core/utils/hooks/dom/window';
import { CalendarEventObject, CalendarEventResponse, CalendarObjectType } from '@business/entities/calendar_event';
import { openAddEditEventModal, viewEventModal } from './AddEditEventModal';
import { FormSelect } from '@core/components/form';
import { UserId, UserRole } from '@business/entities/account';
import { Session } from '@modules/auth/session';
import { CommonService } from '@business/services';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { Col, Row, UncontrolledTooltip } from 'reactstrap';
import { ListView } from '@modules/common/SystemCalendar/TaskChecklist/TaskChecklist';
import { MonthStartEnd } from '@modules/ClientPortal/WorkOrderDashboard/WorkOrderDashboardPage';

// Schedule-imported events use BUSINESS-day durations — render them as weekday segments so the
// bar never covers Sat/Sun. Manually created events keep their exact span. Shared by the render
// and by onEventChange so a drag is measured against exactly what was drawn.
function renderSegments(event: CalendarEventResponse, holidays: HolidaySet): WeekdaySegment[] {
  if (event.importKey) return splitIntoWeekdaySegments(event, holidays);
  return [
    {
      start: event.start,
      // All-day ends are EXCLUSIVE midnights to FullCalendar — an end inside the last day
      // would drop that day from the rendered span, so bump to the following midnight.
      end: event.allDay ? toMoment(event.end).endOf('day').add(1, 'second').unix() : event.end,
      isFirst: true,
    },
  ];
}

/** The end timestamp of an event starting at `start` that covers `businessDays` weekdays, keeping `endTime`'s time of day. */
function endAfterBusinessDays(start: number, businessDays: number, endTime: number, holidays: HolidaySet): number {
  const cursor = toMoment(start).startOf('day');
  let remaining = businessDays - (isWorkingDay(cursor.unix(), holidays) ? 1 : 0);
  while (remaining > 0) {
    cursor.add(1, 'day');
    if (isWorkingDay(cursor.unix(), holidays)) remaining--;
  }
  const time = toMoment(endTime);
  return cursor.hour(time.hour()).minute(time.minute()).second(time.second()).unix();
}

const COLORS = ['#319cf4', '#9e4171', '#10759c', '#8e7c3c', '#4f3f84', '#d52322', '#399121', '#3c17e5', '#a58a3b', '#b84c51', '#7330e6', '#16bc8c'];

function defineRequestForm(
  initialView: CalendarView = CalendarView.month,
  object?: CalendarEventObject,
  isLoginUserEvent?: boolean,
  isClientPublicEvent?: boolean,
) {
  return formGroup({
    start: formControl<number>({ label: 'Start' }),
    end: formControl<number>({ label: 'End' }),
    view: formControl<CalendarView>({ label: 'View', initialValue: initialView }),
    object: formControl<CalendarEventObject>({ label: 'object', initialValue: object }),
    // users: formControl<UserId[]>({ label: 'Users', initialValue: isLoginUserEvent ? [Session.user?.id] : [] }),
    users: formControl<UserId[]>({ label: 'Users', initialValue: isLoginUserEvent ? [] : [] }),
    isNotLoginUserEvent: formControl<boolean>({ initialValue: isLoginUserEvent ? false : true }),
    isClientPublicEvent: formControl<boolean>({ initialValue: isClientPublicEvent }),
  });
}

export interface SystemCalendarProps {
  initialView?: CalendarView;
  object?: CalendarEventObject;
  card?: boolean;
  isShowUserDropDown?: boolean;
  readOnly?: boolean;
  calendarEventsFn?: (params: { data: CalendarEventResponse[] }) => void;
  calendarStartEndDate?: MonthStartEnd;
  isLoginUserEvent?: boolean;
  isClientPublicEvent?: boolean;
  /**
   * View-only: events can be opened and read, but not created, moved, resized, edited or deleted. Defaults to on for
   * the user-wide calendar (no `object` — Todos → System Calendar View), which mixes events from many projects /
   * work orders. Schedules are changed from their own project / work-order calendar, which passes an `object`.
   */
  viewOnly?: boolean;
}

export const SystemCalendar = memo((props: SystemCalendarProps) => {
  const {
    initialView = CalendarView.month,
    card = true,
    object,
    isShowUserDropDown = true,
    calendarEventsFn = null,
    readOnly = false,
    calendarStartEndDate = null,
    isLoginUserEvent = true,
    isClientPublicEvent = false,
  } = props;
  const viewOnly = props.viewOnly ?? !object;
  const form = useMemo(() => defineRequestForm(initialView, object, isLoginUserEvent, isClientPublicEvent), [initialView, object]);
  const formValue = useNonNilObservable(form.value$);
  const bloc = useBloc(SystemCalendarBloc);
  const users = useObservable(bloc.users$, []);
  // const userItems = useMemo(() => CommonService.selectItems(users, u => CommonService.getFullName(u), 'id', true), [users]);

  const userItems = useMemo(() => {
    return CommonService.selectGroupedItems(
      [
        // Subcontractors first, then Staff and Clients
        {
          label: 'Subcontractors',
          items: users
            .filter(u => u.role === UserRole.subcontractor)
            .map(u => ({
              // Name plus company, so either one matches the search
              label: [CommonService.getFullName(u), ...(u.companyNames?.length ? [u.companyNames.join(', ')] : [])].join(' · '),
              value: u.id,
            })),
        },
        {
          label: 'Staff',
          items: CommonService.selectItems(
            users.filter(u => u.role === UserRole.admin || u.role === UserRole.user),
            u => CommonService.getFullName(u),
            'id',
            true,
          ),
        },
        {
          label: 'Clients',
          items: CommonService.selectItems(
            users.filter(u => u.role === UserRole.ClientPortal),
            u => CommonService.getFullName(u),
            'id',
            true,
          ),
        },
      ],
      'label',
      s => s.items,
      'label',
      'value',
    );
    // Must depend on `users`: the list arrives asynchronously (bloc.onInit → fetchUsers), so with an
    // empty dependency array this memo froze on the first render's empty array and the dropdown
    // stayed empty. Inside the SPA the repo was usually already populated by another page, so it only
    // showed on a fresh load (e.g. the Tasks page opened in a new tab).
  }, [users]);

  const userEmails = useMemo(() => {
    return users
      .filter(user => user.id !== Session.user?.id)
      .map(user => ({
        label: `${user.firstName} ${user.lastName}`,
        email: user.email,
        value: user.id,
      }));
  }, [users, Session.user?.id]);

  useEffect(() => {
    if (calendarStartEndDate) {
      form.patchValue({ start: calendarStartEndDate.start, end: calendarStartEndDate.end });
    }
  }, [calendarStartEndDate]);

  const events = useObservable(bloc.events$, []);
  const holidays = useObservable(bloc.holidays$, []);
  // Non-working holiday dates — imported events are split around them like weekends
  const holidaySet = useMemo(() => expandHolidays(holidays), [holidays]);
  // Work-order schedules never have events on weekends/holidays: those cells are greyed out and
  // disabled — no click-to-add, no drop (the drag springs back). Other calendars are unaffected.
  const blockNonWorking = object?.type === CalendarObjectType.workOrder && !readOnly;
  const isWorkingDate = useCallback((date: Date) => isWorkingDay(Math.floor(date.valueOf() / 1000), holidaySet), [holidaySet]);
  const resources = useMemo(() => {
    if (calendarEventsFn) {
      calendarEventsFn({ data: events });
    }
    return R.uniqBy(
      events.map(e => ({ id: e.group, title: e.group })),
      r => r.id,
    ).map((r, i) => ({
      ...r,
      eventBackgroundColor: COLORS[i % COLORS.length],
      eventBorderColor: COLORS[i % COLORS.length],
    }));
  }, [events]);

  const Container = card ? CBody : 'div';
  const { height } = useWindowDimensions();

  const calendarEvents: CalendarEvent[] = useMemo(() => {
    // Company holidays shown by name as locked grey bars (why a schedule skipped a day)
    const holidayBars: CalendarEvent[] = holidayCalendarEvents(holidays);
    // `postFetch` returns one row PER SELECTED USER (each carrying that user's name in `group`), so an
    // event assigned to several of the selected users arrives several times. That is what feeds the
    // per-user columns of the resource view — which this calendar never shows (month/week/day/list
    // only), where it just stacks the same bar N times. Keep one bar per event.
    const seen = new Set<string>();
    return events.reduce<CalendarEvent[]>((acc, event) => {
      if (seen.has(String(event.id))) return acc;
      seen.add(String(event.id));
      renderSegments(event, holidaySet).forEach((segment, index) =>
        acc.push({
          // Stable id per segment so FullCalendar reconciles a moved event instead of keeping a
          // stale copy on the old date next to the new one.
          id: `${event.id}-${index}`,
          start: segment.start,
          end: segment.end,
          title: event.title,
          // Without the flag FullCalendar treats all-day events as timed and prefixes "12a".
          allDay: event.allDay,
          item: event,
          backgroundColor: event.color,
          resourceId: event.group,
          type: 'event',
          // Only the first weekday segment of a split event can be dragged — onEventChange measures
          // the move against it. (Key must be ABSENT, not undefined, on draggable segments.)
          ...(segment.isFirst ? {} : { editable: false }),
        }),
      );
      return acc;
    }, holidayBars);
  }, [events, holidays, holidaySet]);

  // Drag & drop / resize → persist. Before this the calendar was `editable` but nothing saved the
  // move (no request, no toast), so the next refetch — switching Week→Month, changing month —
  // snapped every event back to its old dates.
  const onEventChange = useCallback(
    (_id: string, item: CalendarEventResponse, start: number, end?: number) => {
      if (isHolidayCalendarItem(item)) return; // locked anyway, belt and braces
      const first = renderSegments(item, holidaySet)[0];
      // Schedule events never sit on a weekend/holiday: a drop on Saturday means "the next working
      // day" (Monday), exactly as the import would place it. Manual events keep the day they're given.
      const snappedFrom = start;
      if (blockNonWorking || item.importKey) start = snapToWorkingDay(start, holidaySet);
      // How far the event was dragged, measured against its first rendered segment. All-day events
      // move by whole days (FullCalendar hands back midnights); timed events carry the exact shift.
      const dayShift = toMoment(start).startOf('day').diff(toMoment(first.start).startOf('day'), 'days');
      const shiftSeconds = start - first.start;
      const shifted = (value: number) => (item.allDay ? toMoment(value).add(dayShift, 'days').unix() : value + shiftSeconds);

      const newStart = shifted(item.start);
      // Imported events span BUSINESS days: shifting the end by calendar days would let a Fri–Wed
      // (4-day) event dragged to Monday grow into Mon–Sat. Re-lay the same weekday count from the
      // new start instead, keeping the original end time of day.
      let newEnd = item.importKey
        ? endAfterBusinessDays(newStart, countBusinessDays(item.start, item.end, holidaySet), shifted(item.end), holidaySet)
        : shifted(item.end);

      // A pure drag keeps the rendered span; only a changed span is a RESIZE. Rendered all-day ends
      // are exclusive midnights (see renderSegments) — store the end inside the last day instead.
      // (After a snap the rendered end no longer matches — that's a move, not a resize.)
      const expectedEnd = shifted(first.end);
      if (end && Math.abs(end - expectedEnd) >= 60 && snappedFrom === start) {
        newEnd = item.allDay ? toMoment(end).subtract(1, 'second').unix() : end;
      }

      // Same rule as the edit modal: only a DAY move of a schedule-imported event can cascade to
      // its dependents, so only then ask.
      const askDependents = !!item.importKey && dayShift !== 0;
      const confirm$ = askDependents
        ? showYesNo('Update Schedule', 'Do you want to move the dependent events of this schedule by the same number of days?', {
            btnYesLabel: 'Yes, Update All!',
            btnNoLabel: 'No, just this event!',
          })
        : of(false);

      confirm$
        .pipe(
          // The bloc resyncs the visible range after the save (weekend snap / cascade / stale drag copy)
          switchMap(updateAll => bloc.moveEvent(item.id, { start: newStart, end: newEnd, updateAll })),
        )
        .subscribe({
          // Revert the calendar to the server state if the save failed
          error: () => bloc.fetchEvents(formValue).subscribe(),
        });
    },
    [bloc, formValue, holidaySet, blockNonWorking],
  );

  const [calendarTypes, setCalendarTypes] = useState<CalendarView[]>([CalendarView.month, CalendarView.week, CalendarView.day]);

  const [currentInitialView, setCurrentInitialView] = useState<CalendarView>(CalendarView.month);

  useLazyEffect(
    () => {
      if (!object?.isProviderEvent) {
        if (!formValue.start || !formValue.end) return;
      }
      bloc.fetchEvents(formValue).subscribe();
    },
    [formValue, bloc],
    1000,
  );
  return (
    <Fragment>
      <Container style={{ height: formValue.view === 'dayGridMonth' ? `calc(${height}px - 200px)` : undefined, width: '100%' }}>
        {/* <Container style={{ height: formValue.view === 'dayGridMonth' ? 1500 : undefined, width: '100%' }}> */}
        <Row>
          <Col md="11">
            {isShowUserDropDown && <FormSelect type="multiple" control={form.controls.users} items={userItems} isClearable={false} />}
          </Col>
          <Col md="1" className={`d-flex justify-content-end align-items-center ${!isShowUserDropDown ? 'mb-3' : ''}`} style={{ flex: 1 }}>
            <div className="d-flex align-items-center">
              <span
                className="d-flex hover-pointer ml-3"
                id="grid_view"
                onClick={() => {
                  setCalendarTypes([CalendarView.month, CalendarView.week, CalendarView.day]);
                  setCurrentInitialView(CalendarView.month);
                }}
              >
                <i className={`fal fa-calendar-alt font-size-20`}></i>
                <UncontrolledTooltip placement="top" target={'grid_view'}>
                  System Calendar View
                </UncontrolledTooltip>
              </span>
              <span
                className="d-flex hover-pointer ml-3"
                id="list_view"
                onClick={() => {
                  setCalendarTypes([CalendarView.listMonth, CalendarView.listWeek, CalendarView.listDay]);
                  setCurrentInitialView(CalendarView.listMonth);
                }}
              >
                <i className={`fal fa-th-list font-size-20 `}></i>
                <UncontrolledTooltip placement="top" target={'list_view'}>
                  List View
                </UncontrolledTooltip>
              </span>
            </div>
          </Col>
        </Row>
        <Calendar
          key={currentInitialView}
          initialView={currentInitialView}
          resources={resources}
          stickyHeaderDates
          contentHeight={undefined}
          readOnly={readOnly}
          height={formValue.view === 'resourceTimeGridDay' || formValue.view === 'timeGridWeek' ? height - 175 : 1500}
          nowIndicator
          // All-day events render in the dedicated slot on week/day views (they are not part of the time grid).
          allDaySlot
          onSelect={
            readOnly || viewOnly
              ? undefined
              : (start, end, allDay, viewType) => {
                  openAddEditEventModal({
                    userEmails,
                    start: start.valueOf() / 1000,
                    end: end.valueOf() / 1000,
                    allDay: allDay,
                    object,
                    viewType,
                    isShowUserDropDown,
                  });
                }
          }
          onEventClick={
            readOnly
              ? undefined
              : (id, item: CalendarEventResponse, event) => {
                  if (isHolidayCalendarItem(item)) return; // informational bar
                  viewEventModal({
                    event: item,
                    bloc,
                    userEmails,
                    object,
                    viewOnly, // details only — no Edit / Delete
                  });
                }
          }
          datesSet={info => {
            form.patchValue({
              start: info.start.valueOf() / 1000 - 30 * 60,
              end: info.end.valueOf() / 1000 + 30 * 60,
              view: info.view.type as CalendarView,
            });
          }}
          types={calendarTypes}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            omitZeroMinute: true,
            meridiem: 'short',
            hour12: true,
          }}
          // Fixed row count (not auto-fit): on dense days auto mode fits only a few rows, so a
          // multi-day bar whose first day overflows into "+N more" appears to start a day late.
          dayMaxEventRows={9}
          displayEventTime
          events={calendarEvents}
          onEventChange={readOnly || viewOnly ? undefined : onEventChange}
          // View-only: no drag, resize or create (clicks still open the read-only details)
          {...(viewOnly ? { editable: false, eventStartEditable: false, eventDurationEditable: false, droppable: false, selectable: false } : {})}
          {...(blockNonWorking && !viewOnly
            ? {
                selectAllow: info => isWorkingDate(info.start),
                eventAllow: info => isWorkingDate(info.start),
                dayCellClassNames: arg => (isWorkingDate(arg.date) ? [] : ['calendar-nonworking-day']),
              }
            : {})}
          renderEventContent={e =>
            !e.view.type.startsWith('list') ? (
              true
            ) : (
              <ListView
                event={e}
                updateEventUserTask={(userId, isAdmin) => {
                  bloc.updateEventUserTask(R.toString(userId), isAdmin).subscribe();
                }}
              />
            )
          }
        />
      </Container>
    </Fragment>
  );
});
